import { openMenuAndGet, setReactInputValue } from '../../shared/domHelpers'
import { demoRoutes, dismissSuccessAndContinue } from '../DemoFlow'
import demoStore from '../mock/DemoStore'
import { skipStageConfig } from '../demoSkipStage'
import { disablePopoverButtons } from '../../tourUi'
import { withNativeDemoSaveAllowed } from '../demoSaveControls'

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))
const SUCCESS_MODAL_READING_TIME = 500
const LISTA_READY = '[data-tour="perfil-cliente-novo"], [data-tour="perfil-cliente-lista"], [data-tour="help-perfil-cliente-grid"]'
const FORM_READY = '[data-tour="perfil-cliente-form-root"], [data-tour="perfil-cliente-nome"] input, input[name="nome"]'

const getMenuPerfisCliente = () =>
    openMenuAndGet('cliente', 'group-cliente', '[data-tour="menu-perfis-cliente"]')

const fillNome = () => {
    const nomeInput =
        document.querySelector('[data-tour="perfil-cliente-nome"] input') ||
        document.querySelector('input[name="nome"]')
    if (nomeInput) setReactInputValue(nomeInput, 'Perfil Demonstração')
}

const fillPermissoes = () => {
    const checkboxes = document.querySelectorAll(
        '[data-tour="perfil-cliente-permissao"] input[type="checkbox"], [data-tour="perfil-cliente-modulos"] input[type="checkbox"]'
    )
    checkboxes.forEach((checkbox) => {
        if (!checkbox.checked) checkbox.click()
    })
}

const navigateSoft = async (tourManager, path, selector, resumeIndex) => {
    if (document.querySelector(selector)) return

    tourManager.internalNavigation = true
    try {
        if (!tourManager.history) {
            return tourManager.navigateAndWait(path, selector, resumeIndex, 'demo-perfil-cliente')
        }

        tourManager.history.push(path)
        await tourManager.waitForElement(selector, 8000)
        tourManager.tourPath = tourManager.currentPath()
    } finally {
        tourManager.internalNavigation = false
    }
}

const openNovoPerfil = (tourManager) => navigateSoft(tourManager, demoRoutes.perfilNovo, FORM_READY, 2)

const openListaPerfil = (tourManager) => navigateSoft(tourManager, demoRoutes.perfilLista, LISTA_READY, 1)

const waitSuccessModal = (tourManager, timeout = 8000) =>
    tourManager.waitForElement(() => {
        const body = document.querySelector('.modal.show .modal-body, .modal.show .modal-content')
        if (body && /sucesso|criado|editado/i.test(body.textContent || '')) return body
        return null
    }, timeout)

const waitForModalToClose = (tourManager, timeout = 2000) =>
    tourManager.waitForElement(() => (!document.querySelector('.modal.show') ? document.body : null), timeout)

const closeSuccessModalSoft = () => {
    const buttons = Array.from(document.querySelectorAll('.modal.show .modal-footer button, .modal.show button'))
    const fechar = buttons.find((button) => /^fechar$/i.test((button.textContent || '').trim()))
    if (fechar) {
        fechar.click()
        return
    }
    document.querySelector('.modal.show .close')?.click()
}

const prepareAndSavePerfil = async (tourManager) => {
    if (tourManager._hdvSavingPerfil) return
    tourManager._hdvSavingPerfil = true
    disablePopoverButtons({ next: true, prev: true })

    try {
        fillNome()
        fillPermissoes()
        await delay(120)

        const saveButton = document.querySelector('[data-tour="perfil-cliente-salvar"]')
        if (saveButton) {
            withNativeDemoSaveAllowed(saveButton, () => saveButton.click())
        }

        try {
            await waitSuccessModal(tourManager)
            await delay(SUCCESS_MODAL_READING_TIME)

            if (document.querySelector('.modal.show')) {
                closeSuccessModalSoft()
                await waitForModalToClose(tourManager)
            }

            tourManager.internalNavigation = true
            try {
                if (tourManager.history) {
                    tourManager.history.replace(demoRoutes.perfilLista)
                }

                await tourManager.waitForElement(LISTA_READY, 5000)
                tourManager.tourPath = tourManager.currentPath()
                await delay(200)
            } finally {
                tourManager.internalNavigation = false
            }
        } catch (error) {
            tourManager.internalNavigation = true
            try {
                if (tourManager.history) {
                    tourManager.history.replace(demoRoutes.perfilLista)
                    await delay(250)
                    tourManager.tourPath = tourManager.currentPath()
                }
            } finally {
                tourManager.internalNavigation = false
            }
        }

        if (!demoStore.state.perfil) {
            demoStore.createPerfil({ nome: 'Perfil Demonstração' })
        }

        tourManager.moveNext()
    } finally {
        setTimeout(() => {
            tourManager._hdvSavingPerfil = false
        }, 500)
    }
}

const buildDemoPerfilClienteTourSteps = (tourManager) => [
    {
        element: getMenuPerfisCliente,
        ensureReady: () => {
            const menuItem = getMenuPerfisCliente()
            if (menuItem) return Promise.resolve(menuItem)
            return tourManager.waitForElement(getMenuPerfisCliente)
        },
        skipStage: skipStageConfig('clienteLista'),
        popover: {
            title: 'Perfis de cliente',
            description:
                'Agora vamos criar um perfil de cliente com as permissões utilizadas na demonstração. Esse perfil será associado ao cliente nas próximas etapas.<br><br>' +
                '<strong>Pular etapa</strong> conclui este cadastro automaticamente e avança para a próxima etapa.',
            side: 'right',
            align: 'start',
            onNextClick: () => {
                openListaPerfil(tourManager)
                    .then(() => tourManager.moveNext())
                    .catch(() => tourManager.moveNext())
            },
        },
    },
    {
        element: '[data-tour="perfil-cliente-novo"]',
        ensureReady: () => tourManager.waitForElement(LISTA_READY),
        popover: {
            title: 'Lista de perfis',
            description: 'Aqui ficam os perfis cadastrados. Clique em <strong>Próximo</strong> para iniciar um novo perfil de demonstração.',
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
                openNovoPerfil(tourManager)
                    .then(() => tourManager.moveNext())
                    .catch(() => tourManager.moveNext())
            },
        },
    },
    {
        element: '[data-tour="perfil-cliente-nome"]',
        ensureReady: () => tourManager.waitForElement(FORM_READY),
        popover: {
            title: 'Nome do perfil',
            description: 'Vamos preencher o nome que identifica este conjunto de permissões.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillNome()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="perfil-cliente-modulos"]',
        popover: {
            title: 'Permissões',
            description: 'Marcamos algumas permissões essenciais para o perfil demo.',
            side: 'top',
            align: 'start',
            onNextClick: async () => {
                fillPermissoes()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="perfil-cliente-salvar"]',
        popover: {
            title: 'Salvar perfil',
            description: 'Clique em <strong>Salvar</strong>, o guia vai gravar este perfil e, no próximo passo, segue para o cliente.',
            side: 'top',
            align: 'end',
            nextBtnText: 'Salvar',
            onNextClick: () => prepareAndSavePerfil(tourManager),
        },
    },
    {
        popover: {
            title: 'Perfil salvo',
            description: 'Perfil criado. Em <strong>Ir para cliente</strong> abrimos o cadastro de cliente.',
            side: 'over',
            align: 'center',
            nextBtnText: 'Ir para cliente',
            onNextClick: () => {
                if (!demoStore.state.perfil) {
                    demoStore.createPerfil({ nome: 'Perfil Demonstração' })
                }
                dismissSuccessAndContinue(tourManager, {
                    fromTour: 'demo-perfil-cliente',
                    toTour: 'demo-cliente',
                    waitSelector: '[data-tour="menu-lista-clientes"], [data-tour="cliente-novo"], [data-tour="cliente-lista"]',
                    fallbackPath: demoRoutes.clienteLista,
                })
            },
        },
    },
    {
        popover: {
            title: 'Continuando…',
            description: 'Abrindo o cadastro de cliente…',
            showButtons: ['close'],
        },
    },
]

export default buildDemoPerfilClienteTourSteps
